/**
 * 안전한 Web Storage wrapper.
 *
 * 모바일 시크릿 모드, 인앱 WebView, 추적 차단 강 설정 등
 * `typeof localStorage !== 'undefined'` 가드만으로는 막히지 않는 환경에서
 * `localStorage.getItem` 호출 자체가 SecurityError 를 throw 하는 경우가 있다.
 * 모든 접근을 try/catch 로 감싸 throw 가 컴포넌트 트리/Zustand persist 까지
 * 전파되는 것을 방지한다.
 */

interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function getNativeLocal(): Storage | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    return window.localStorage
  } catch {
    return undefined
  }
}

function getNativeSession(): Storage | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    return window.sessionStorage
  } catch {
    return undefined
  }
}

function createSafe(getNative: () => Storage | undefined): StorageLike {
  return {
    getItem(key) {
      try {
        return getNative()?.getItem(key) ?? null
      } catch {
        return null
      }
    },
    setItem(key, value) {
      try {
        getNative()?.setItem(key, value)
      } catch {
        /* SecurityError, QuotaExceededError 등 무시 */
      }
    },
    removeItem(key) {
      try {
        getNative()?.removeItem(key)
      } catch {
        /* ignore */
      }
    },
  }
}

export const safeLocalStorage: StorageLike = createSafe(getNativeLocal)
export const safeSessionStorage: StorageLike = createSafe(getNativeSession)
